import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";

import { modelMap, EntityDropDownSettings, STATIC_ENTITES, DROPDOWN_PAGE_SIZE, FILTER_DEBOUNCE_MS } from "../config/config";
import { ENTITY_DROPDOWN_SETTINGS } from "../config/lang";

import { call } from "../util/action";
import { setDefaultTemplate } from "../util/redux/actions";
import { saveState } from "../util/redux/localStorage";
import { getStore } from "../util/redux/store";
import { maptoSelect2Objects, translate } from "../util/util";

// Entities whose options are built locally (no server list to page/search through).
const LOCAL_ENTITIES = ["task_negative", "task_status", "task_deadline", "date_range", "language"];

class EntityDropdown extends Component {
  static defaultProps = {
    asynch: false,
    useFullOptionObject: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      list: [],
      searchTerm: "",
      keyTrick: 1, // used to trigger new loadOptions,
      loading: false, // options are being fetched (shows the select spinner)
      loaded: false, // options have been fetched once; don't refetch on re-open
    };

    this.customStyles = {
      menu: base => ({
        ...base,
        zIndex: 9999,
        minWidth: "220px",
      }),
      menuList: base => ({
        ...base,
        maxHeight: "260px",
      }),
      menuPortal: base => ({
        ...base,
        zIndex: 9999,
      }),
    };
  }

  componentDidMount() {
    // Parent supplies the list directly (e.g. photo-review "filters") — use it as-is, no fetch.
    if (this.props.updatedList) {
      this.setState({ list: this.props.updatedList, loaded: true });
      return;
    }
    if (this.usesPagination()) {
      // AsyncPaginate fetches its first page lazily when the menu opens. Only seed via the API
      // when we have a bare-id value that must be resolved to a label — a predetermined value
      // passed as a full object renders directly (see formatValue), with no API call.
      if (this.needsApiSeed()) {
        this.loadPaginatedOptions("", [], { page: 1 });
      }
      return;
    }
    // Simple Select path (local/static lists + "template"): load eagerly only when needed;
    // otherwise the list is built on the first menu open (see handleMenuOpen).
    if (this.shouldLoadEagerly()) {
      this.fetchData();
    }
  }

  // Network-backed list entities use the paginated + searchable AsyncPaginate select.
  // Kept on the simple Select path: locally-built lists (static + language), "template"
  // (defaultTemplate side effect + useDefaultValue), and parent-injected lists (updatedList).
  usesPagination = () => {
    const { entity, updatedList } = this.props;
    return !updatedList && entity !== "template" && !LOCAL_ENTITIES.includes(entity);
  };

  // Eager-load conditions for the simple Select path: a value is already selected (so its label
  // can render), or the "template" entity (defaultTemplate side effect + useDefaultValue).
  shouldLoadEagerly = () => {
    const v = this.props.value;
    const hasValue = Array.isArray(v) ? v.length > 0 : v !== "" && v !== null && v !== undefined;
    return this.props.entity === "template" || hasValue;
  };

  // The logged-in supervisor's default region (county) rides on `me` from login as a full
  // object, so we can pre-select AND label it without any backend request.
  defaultRegionOption = () => {
    const c = this.props.me && this.props.me.county;
    const isCounty = this.props.entity === "county" || this.props.entity === "counties";
    if (isCounty && c && typeof c === "object" && c.id != null && c.name != null) return c;
    return null;
  };

  // True only for a bare-id/primitive preselected value that must be resolved to a label via the
  // API. A predetermined value given as a full object (or array of objects) is self-describing
  // and renders with no request.
  needsApiSeed = () => {
    const v = this.props.value;
    if (v === "" || v === null || v === undefined) return false;
    if (Array.isArray(v)) return v.length > 0 && typeof v[0] !== "object";
    // The default region's label comes from `me` — no seed request needed to show it.
    const dr = this.defaultRegionOption();
    if (dr && String(dr.id) === String(v)) return false;
    return typeof v !== "object";
  };

  // Simple Select path only: build/fetch the list the first time the menu opens, then reuse it.
  handleMenuOpen = () => {
    if (!this.usesPagination() && !this.props.updatedList && !this.state.loaded && !this.state.loading) {
      this.fetchData();
    }
  };

  // Merge freshly loaded options into the cache used by formatValue for label lookup.
  mergeOptions = (existing, incoming) => {
    const byValue = new Map((existing || []).map(o => [o.value, o]));
    incoming.forEach(o => byValue.set(o.value, o));
    return Array.from(byValue.values());
  };

  componentDidUpdate(prevProps) {
    const { entity, county, moduleFilter, updatedList } = this.props;
    // Remount the paginated select (and drop cached options) when the entity/scope changes,
    // otherwise AsyncPaginate keeps serving the options it first loaded — e.g. switching the
    // survey audience type from Point groups to Points/Networks still showed point groups.
    if (prevProps.entity !== entity || prevProps.county !== county || prevProps.moduleFilter !== moduleFilter) {
      this.setState(prevState => ({
        keyTrick: prevState.keyTrick + 1,
        list: [],
      }));
    }

    if (prevProps.updatedList !== updatedList) {
      this.setState({ list: updatedList });
    }
  }

  fetchData = () => {
    let { entity } = this.props;
    if (["task_negative", "task_status", "task_deadline"].includes(entity)) {
      this.setState({ list: maptoSelect2Objects(STATIC_ENTITES[entity], entity), loaded: true });
      return;
    }

    let url = modelMap[entity];
    // Note: point/points and brands are no longer served here — they use the paginated,
    // searchable AsyncPaginate path (DROPDOWN_PAGE_SIZE per page), so the old
    // "?page_size=1000" fetch-everything shortcut for brands has been removed.
    if (entity == "language") {
      url = "users/";
    }
    //Custom Select Data for Date Range on Visit section
    if (entity == "date_range") {
      const dateAgoList = [
        {
          id: 0,
          label: translate(ENTITY_DROPDOWN_SETTINGS, "date_today", this.props.me?.language ? this.props.me.language : "en"),
          value: this.props.setDateAgo("today"),
          name: translate(ENTITY_DROPDOWN_SETTINGS, "date_today", this.props.me?.language ? this.props.me.language : "en"),
        },
        {
          id: 1,
          label: translate(ENTITY_DROPDOWN_SETTINGS, "date_yesterday", this.props.me?.language ? this.props.me.language : "en"),
          value: this.props.setDateAgo("yesterday"),
          name: translate(ENTITY_DROPDOWN_SETTINGS, "date_yesterday", this.props.me?.language ? this.props.me.language : "en"),
        },
        {
          id: 2,
          label: translate(ENTITY_DROPDOWN_SETTINGS, "date_current_week", this.props.me?.language ? this.props.me.language : "en"),
          value: this.props.setDateAgo("current_week"),
          name: translate(ENTITY_DROPDOWN_SETTINGS, "date_current_week", this.props.me?.language ? this.props.me.language : "en"),
        },
        {
          id: 3,
          label: translate(ENTITY_DROPDOWN_SETTINGS, "date_last_week", this.props.me?.language ? this.props.me.language : "en"),
          value: this.props.setDateAgo("last_week"),
          name: translate(ENTITY_DROPDOWN_SETTINGS, "date_last_week", this.props.me?.language ? this.props.me.language : "en"),
        },
        {
          id: 4,
          label: translate(ENTITY_DROPDOWN_SETTINGS, "date_current_month", this.props.me?.language ? this.props.me.language : "en"),
          value: this.props.setDateAgo("current_month"),
          name: translate(ENTITY_DROPDOWN_SETTINGS, "date_current_month", this.props.me?.language ? this.props.me.language : "en"),
        },
        {
          id: 5,
          label: translate(ENTITY_DROPDOWN_SETTINGS, "date_last_month", this.props.me?.language ? this.props.me.language : "en"),
          value: this.props.setDateAgo("last_month"),
          name: translate(ENTITY_DROPDOWN_SETTINGS, "date_last_month", this.props.me?.language ? this.props.me.language : "en"),
        },
        {
          id: 6,
          label: translate(ENTITY_DROPDOWN_SETTINGS, "date_last_half_year", this.props.me?.language ? this.props.me.language : "en"),
          value: this.props.setDateAgo("last_half_a_year"),
          name: translate(ENTITY_DROPDOWN_SETTINGS, "date_last_half_year", this.props.me?.language ? this.props.me.language : "en"),
        },
      ];
      this.setState({ list: dateAgoList, loaded: true });
    } else {
      this.setState({ loading: true });
      call({ method: "GET", url }, response => {
        if (
          entity == "filters" ||
          entity == "absence_reason_templates" ||
          entity == "users" ||
          entity == "task" ||
          entity == "visit" ||
          entity == "point" ||
          entity == "points" ||
          entity == "point_groups" ||
          entity == "brand" ||
          entity == "brands" ||
          entity == "product" ||
          entity == "products" ||
          entity == "categories" ||
          entity == "subcategory" ||
          entity == "subcategories"
        ) {
          response = response.results;
        }

        if (entity === "language") {
          //Set languages as default
          response = [
            { id: 1, code: "uk", name: "Українська" },
            { id: 2, code: "en", name: "English" },
            { id: 3, code: "hy", name: "Հայերեն" },
            { id: 4, code: "ka", name: "ქართული" },
          ];
        }

        response = maptoSelect2Objects(response, entity);
        this.setState({ list: response, loading: false, loaded: true });
        if (entity === "template") {
          let defaultOption = response?.find(item => item.is_default);
          getStore().dispatch(setDefaultTemplate(defaultOption));
          saveState({
            ...getStore().getState(),
            defaultTemplate: defaultOption?.id,
          });
        }
      });
    }
  };

  formatValue = value => {
    if (this.props.isMulti) {
      const userEntities = ["created_by", "supervisor", "supervisors", "merchandiser", "merchandisers"];
      return (value || [])
        .map(i => {
          // Tolerate bare ids/null: resolve ids against loaded options, drop what we can't build.
          if (i === null || i === undefined) return null;
          if (typeof i !== "object") {
            const found = (this.state.list || []).find(o => o.id === i);
            if (!found) return null;
            i = found;
          }
          if (i.name) {
            return { ...i, value: i.id, label: i.name };
          } else if (userEntities.includes(this.props.entity) && (i.first_name || i.last_name)) {
            return { ...i, value: i.id, label: (i.first_name || "") + " " + (i.last_name || "") };
          } else {
            return { ...i, value: i.id ?? i.value, label: i.label };
          }
        })
        .filter(Boolean);
    }

    // A predetermined value passed as a full object is self-describing: render it directly from
    // the object (no state.list lookup, no API call). Bare ids/strings fall back to a lookup.
    const valueIsObject = value !== null && value !== undefined && typeof value === "object";

    // Default region: show the supervisor's county name straight from `me` (no fetch), so the
    // filter reads as active even though the user never opened it.
    if (!valueIsObject && value !== null && value !== undefined && value !== "") {
      const dr = this.defaultRegionOption();
      if (dr && String(dr.id) === String(value)) return { value: dr.id, label: dr.name };
    }

    let defaultOption = this.state.list?.find(item => item.is_default);
    let option = valueIsObject ? value : this.state.list.find(o => o.id === value);

    if (!valueIsObject && this.props.entity == "language" && value) {
      option = this.state.list.find(o => o.code === value);
    }

    if (!valueIsObject && this.props.entity == "date_range" && value) {
      option = this.state.list.find(o => o.value === value);
    }

    if (this.props.useFullOptionObject) {
      option = value;
    }
    if (this.props.useDefaultValue && value === "") {
      option = defaultOption;
    }

    if (!valueIsObject && this.props.entity == "absence_reason_templates") {
      option = this.state.list.find(o => o.label === value);
    }

    if (option) {
      let label = option.name || option.label || "";
      if (this.props.entity == "supervisors") label = option.first_name + " " + option.last_name;
      if (this.props.entity == "merchandiser") label = option.first_name + " " + option.last_name;
      if (this.props.entity == "merchandisers") label = option.first_name + " " + option.last_name;
      if (this.props.entity == "supervisor") label = option.first_name + " " + option.last_name;
      if (this.props.entity == "created_by") label = option.first_name + " " + option.last_name;
      if (this.props.entity == "point") label = option.name + ", " + option.locality?.name;
      if (this.props.entity == "points") label = option.name + ", " + option.locality?.name;
      if (this.props.entity == "absence_reason_templates") label = option.label;
      return { value: option.id ?? option.value, label };
    } else {
      return null;
    }
  };

  // Paginated + searchable loader for AsyncPaginate: fetches one page (DROPDOWN_PAGE_SIZE)
  // at a time, searching server-side via `?search=`, and reports whether more pages exist
  // so the menu can load the next page on scroll.
  loadPaginatedOptions = async (search, loadedOptions, additional) => {
    const page = (additional && additional.page) || 1;
    const { entity, county, moduleFilter } = this.props;

    const base = String(modelMap[entity] || entity).replace(/\/+$/, "");
    let url = `${base}?page=${page}&page_size=${DROPDOWN_PAGE_SIZE}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (entity === "point" || entity === "points") {
      url += `&locality__county=${county || ""}&network=${moduleFilter || ""}`;
    }
    // Optional per-field extra query params (e.g. the Region form requests active-only countries).
    if (this.props.extraQuery) {
      Object.entries(this.props.extraQuery).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") url += `&${k}=${encodeURIComponent(v)}`;
      });
    }

    const response = await new Promise(resolve => call({ method: "GET", url }, resolve));
    const rawItems = Array.isArray(response) ? response : (response && response.results) || [];
    const options = maptoSelect2Objects(rawItems.slice(), entity) || [];

    // Keep every option we've loaded so formatValue can resolve the selected value's label.
    this.setState(prev => ({ list: this.mergeOptions(prev.list, options) }));

    const hasMore = Array.isArray(response) ? false : Boolean(response && response.next);
    return { options, hasMore, additional: { page: page + 1 } };
  };

  render() {
    const { keyTrick } = this.state;
    let className = this.props.className ? this.props.className : "";

    return (
      <div className={`react-dropdown-container ${className}`}>
        {this.usesPagination() ? (
          <AsyncPaginate
            key={keyTrick}
            value={this.formatValue(this.props.value) || null}
            loadOptions={this.loadPaginatedOptions}
            additional={{ page: 1 }}
            debounceTimeout={FILTER_DEBOUNCE_MS}
            isMulti={this.props.isMulti || false}
            placeholder={
              this.props.placeholder
                ? this.props.placeholder
                : translate(
                    ENTITY_DROPDOWN_SETTINGS,
                    EntityDropDownSettings[this.props.entity]?.selectTitle,
                    this.props.me?.language ? this.props.me.language : "en"
                  )
            }
            onChange={this.props.onChange}
            isClearable={this.props.isClearable ?? true}
            isDisabled={this.props.isDisabled}
            styles={this.customStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            className="react-select-custom-container"
            classNamePrefix="custom-react-select"
          />
        ) : (
          <Select
            options={this.state.list}
            onMenuOpen={this.handleMenuOpen}
            isLoading={this.state.loading}
            isMulti={this.props.isMulti}
            value={this.formatValue(this.props.value) || []}
            // placeholder={this.props.placeholder ? this.props.placeholder : EntityDropDownSettings[this.props.entity]?.selectTitle}
            placeholder={
              this.props.placeholder
                ? this.props.placeholder
                : translate(
                    ENTITY_DROPDOWN_SETTINGS,
                    EntityDropDownSettings[this.props.entity]?.selectTitle,
                    this.props.me?.language ? this.props.me.language : "en"
                  )
            }
            onChange={this.props.onChange}
            isClearable={this.props.isClearable ?? true}
            isDisabled={this.props.isDisabled}
            styles={this.customStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            className="react-select-custom-container"
            classNamePrefix="custom-react-select"
          />
        )}
      </div>
    );
  }
}

export default withRouter(connect(state => ({ me: state.me }))(EntityDropdown));
